import { UniversalVisualizatorParser } from "./universal-visualizator-parser";
import { UniversalPlotProvider } from "./universal-plot-provider";
import { UniversalRhythmPlotProvider } from "./universal-rhythm-plot-provider";
import { Plot } from "../../../../web-visualizator";
import { BaseVisualizatorProvider } from "../core/base-visualizator-provider";

export class UniversalVisualizatorProvider extends BaseVisualizatorProvider {
  constructor() {
    super(new UniversalVisualizatorParser());
  }

  init(settings, saveableUserSettings) {
    super.init(settings, saveableUserSettings);

    this.addParserCallback(
      "beforeObjectsCreation",
      null,
      function (parser) {
        let canvasCreator = parser.visualizatorCreator.canvasCreator;
        canvasCreator.addCallback(
          "beforeAddItems",
          function (canvasObject) {
            this.canvasObject = canvasObject;
          }.bind(this)
        );
        let plotCreator = canvasCreator.viewObjectCreators.get("plot:universal:base");
        plotCreator?.addCallback("afterCreate", this.afterAddItemAction.bind(this));
        plotCreator = canvasCreator.viewObjectCreators.get("plot:universal:rhythm");
        plotCreator?.addCallback("afterCreate", this.afterAddItemAction.bind(this));
      }.bind(this)
    );
  }

  afterAddItemAction(item, viewObjectSettings /*, creator*/) {
    if (viewObjectSettings === undefined) {
      return;
    }

    let plotInfo = undefined;
    let plotControlsSettings = Plot.findContentSettings(item.settings, "plotControls", "custom");
    const linkedChannelDataSource =
      plotControlsSettings.linkedChannel.id_dataSource ??
      (viewObjectSettings.dataSources.length > 0 ? viewObjectSettings.dataSources[0].id : undefined);
    item.settings._linkedChannel = {
      id_dataSource: linkedChannelDataSource,
      id:
        plotControlsSettings.linkedChannel.id ??
        (linkedChannelDataSource === undefined || linkedChannelDataSource.channels.length == 0
          ? undefined
          : linkedChannelDataSource.channels[0].id)
    };
    if (viewObjectSettings.type == "plot:universal:base") {
      plotInfo = new UniversalPlotProvider(viewObjectSettings._id, viewObjectSettings.type, item);
    } else if (viewObjectSettings.type == "plot:universal:rhythm") {
      plotInfo = new UniversalRhythmPlotProvider(viewObjectSettings._id, viewObjectSettings.type, item);
    }
    plotInfo.init();
    plotInfo.onViewSettingsChanged = this.onViewSettingsChanged;
    plotInfo.viewSettings.width = item.settings.sizeInPixels.width;
    plotInfo.viewSettings.x = item.settings.absoluteLocation.x;
    plotInfo.viewSettings.y =
      item.settings.canvas.settings.sizeInPixels.height - item.settings.absoluteLocation.y - item.settings.sizeInPixels.height;

    plotInfo.viewSettings.controlsHeight = plotControlsSettings.height;
    plotInfo.viewSettings.controlsMargin = plotControlsSettings.margin;
    plotInfo.viewSettings.height = plotControlsSettings.height;

    item.setOnSelectChannel((dataSourceId, channelId) => {
      plotInfo.selectChannel(dataSourceId, channelId);
    });
    plotInfo.saveableUserSettings = this.saveableUserSettings;
    this.plotsInfo.push(plotInfo);
    for (let dataSourceIndex = 0; dataSourceIndex < viewObjectSettings.dataSources.length; dataSourceIndex++) {
      const dataSourceSettings = viewObjectSettings.dataSources[dataSourceIndex];
      const dataSourcesMappedInfo = this.dataSourcesMap.get(dataSourceSettings.id);
      for (let channelIndex = 0; channelIndex < dataSourceSettings.channels.length; channelIndex++) {
        const plotChannelSettings = dataSourceSettings.channels[channelIndex];
        this._addPlotIdToChannelMappedInfo(dataSourcesMappedInfo, plotChannelSettings, viewObjectSettings._id, plotId => {
          return { id: plotId };
        });
      }
      for (let markIndex = 0; markIndex < dataSourceSettings.marks.length; markIndex++) {
        const plotMarkSettings = dataSourceSettings.marks[markIndex];
        this._addPlotIdToMarkMappedInfo(dataSourcesMappedInfo, plotMarkSettings, viewObjectSettings._id);
      }
    }
    item.onSignalLabelClicked(plotInfo.viewSettings.linkedChannel.id_dataSource, plotInfo.viewSettings.linkedChannel.id);
  }

  setMeanderUnit(sourceId, channelName, unit) {
    let dataSourceId = sourceId ?? 0;
    let channelId = this.getChannelId(dataSourceId, channelName);
    this.forEachChannelPlot(
      dataSourceId,
      channelId,
      function (plotInfo) {
        plotInfo.setMeanderUnit(unit);
      }.bind(this)
    );
  }

  addMarkedEvent(type, data) {
    if (this.plotsInfo.length == 0) {
      return;
    }
    this.addMark(type);
    this.pushEventMessage([
      {
        type: type,
        data: data
      }
    ]);
  }

  addMark(markType) {
    if (this.plotsInfo.length == 0) {
      return;
    }
    this.pushMarkMessage({
      type: "mark",
      markType: markType,
      pointCounter: this.plotsInfo[0].webglPlot.onlinePlotObjects.lastFramePointCounter
    });
  }

  pushMessage(message) {
    if (message.type === "adc") {
      this.pushAdcMessage(message);
    } else if (message.type === "event") {
      this.pushEventMessage(message.data);
    } else if (message.type === "mark") {
      this.pushMarkMessage(message);
    }
  }

  pushAdcMessage(message, getPhysicValueFunc, sourceId) {
    this._addPointsToPlots(sourceId ?? 0, message, getPhysicValueFunc);
    this.refreshGraphics();
  }

  pushEventMessage(data, sourceId) {
    for (let eventIndex = 0; eventIndex < data.length; eventIndex++) {
      let eventInfo = data[eventIndex];
      this._addMarkToPlots(sourceId ?? 0, eventInfo.type, eventInfo.data);
      // user custom mark handlers
      let handler = this.eventHandlers.get(eventInfo.type);
      handler?.(eventInfo.data);
    }
  }

  pushMarkMessage(message) {
    for (let i = 0; i < this.plotsInfo.length; i++) {
      this.plotsInfo[i].webglPlot.addMark(message);
      this.onAddMark?.(message);
    }
  }
}
